import { useI18n } from '@ecom/i18n';
import { EmptyState } from '@ecom/ui-kit';
import { type ReactElement, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import type { TopProduct } from '@/features/analytics/types';
import { fetchProductImageObjectUrl, useProductImagesQuery } from '@/features/products/api';
import { formatCatalogPrice } from '@/features/products/format-price';
import { useSession } from '@/lib/session';

/**
 * Resolves a product's FIRST real uploaded image (Images Produits lot) to
 * a displayable blob URL — same pattern `CartItemsList.tsx` (storefront)
 * already established: `<img src>` cannot carry the `Authorization`
 * header this route requires, so the bytes are fetched here and handed
 * back as a local object URL, revoked on unmount/replacement. A product
 * with no uploaded image falls back to the same honest letter-tile every
 * other card in this app already uses — never a fabricated photo.
 */
function TopProductThumbnail({
  productId,
  productName,
}: {
  productId: string;
  productName: string;
}): ReactElement {
  const { accessToken, activeShopId } = useSession();
  const imagesQuery = useProductImagesQuery(productId);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const firstImageId = imagesQuery.data?.[0]?.id;

  useEffect(() => {
    if (firstImageId === undefined || accessToken === null || activeShopId === null) return;
    let cancelled = false;
    let createdUrl: string | null = null;
    void fetchProductImageObjectUrl(productId, firstImageId, accessToken, activeShopId).then(
      (url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        createdUrl = url;
        setObjectUrl(url);
      },
      () => {
        // No usable image — the letter-tile fallback below covers it.
      },
    );
    return () => {
      cancelled = true;
      if (createdUrl !== null) URL.revokeObjectURL(createdUrl);
    };
  }, [productId, firstImageId, accessToken, activeShopId]);

  return (
    <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-surface2">
      {objectUrl !== null ? (
        <img src={objectUrl} alt={productName} className="h-full w-full object-cover" />
      ) : (
        <div
          className="flex h-full w-full items-center justify-center text-inkFaint"
          aria-hidden="true"
        >
          <span className="font-display text-sm">{productName.charAt(0).toUpperCase()}</span>
        </div>
      )}
    </div>
  );
}

/**
 * "Bloc Top Produits" — Sprint UX/UI Merchant Dashboard V2: photo réelle,
 * nom, ventes, montant. Data is `MerchantAnalyticsOverview.topProducts`
 * (top 5 by quantity sold, CANCELLED excluded) — already fetched by the
 * caller via `useMerchantAnalyticsQuery()`, passed in rather than
 * re-fetched here.
 */
export function TopProducts({ products }: { products: readonly TopProduct[] }): ReactElement {
  const { t } = useI18n();
  // The list is ranked by units sold (the API's top five by quantity), so the bars measure units sold too.
  const topQuantity = Math.max(...products.map((product) => product.quantitySold), 0);
  return (
    <section
      className="sk-panel flex h-full flex-col p-[21px]"
      aria-labelledby="top-products-title"
    >
      <div className="sk-section-head">
        <div>
          <span className="sk-eyebrow">{t('merchant.dashboard.topEyebrow')}</span>
          <h2 id="top-products-title">{t('merchant.dashboard.topProducts.title')}</h2>
        </div>
      </div>
      {products.length === 0 ? (
        <div className="mt-3">
          <EmptyState
            title={t('merchant.dashboard.topProducts.emptyTitle')}
            description={t('merchant.dashboard.topProducts.emptyDescription')}
          />
        </div>
      ) : (
        <ol className="-mx-2 mt-2 grid gap-x-6 lg:grid-cols-2">
          {products.map((product, index) => (
            <li key={product.productId}>
              <Link
                to={`/products/${product.productId}`}
                className="press flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-surfaceHover"
              >
                <TopProductThumbnail
                  productId={product.productId}
                  productName={product.productName}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-ink">
                    {product.productName}
                  </span>
                  <span className="block text-[13px] text-inkFaint">
                    {t('merchant.dashboard.topProducts.soldCount', {
                      count: product.quantitySold,
                      plural: product.quantitySold > 1 ? 's' : '',
                    })}
                  </span>
                  {/* Most sold first, seen and not only read: a 2px bar of its units against the best seller's,
                      the best seller in the dye, the others grey. */}
                  <span
                    className="mt-1.5 block h-0.5 overflow-hidden rounded-full bg-surface2"
                    aria-hidden="true"
                  >
                    <span
                      className={`anim-grow-x block h-full rounded-full ${index === 0 ? 'bg-[var(--brand)]' : 'bg-[color-mix(in_srgb,var(--brand)_28%,var(--ecom-line))]'}`}
                      style={{
                        width: `${String(topQuantity > 0 ? (product.quantitySold / topQuantity) * 100 : 0)}%`,
                      }}
                    />
                  </span>
                </span>
                <span className="figure shrink-0 text-[15px] font-semibold text-ink">
                  {formatCatalogPrice(product.revenue)}
                </span>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
